import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AppShell from 'components/AppShell';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

// ─── Hero Section ─────────────────────────────────────────────────────────────

function AboutHero() {
    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#1e2d3d',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '100px 32px 80px',
                textAlign: 'center',
            }}
        >
            <Typography
                component="h1"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 800,
                    fontSize: '3rem',
                    color: '#FFFFFF',
                    mb: '24px',
                    maxWidth: '800px',
                    lineHeight: 1.25,
                }}
            >
                Why We Built IELTS BandBoost
            </Typography>
            <Typography
                component="p"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: 'rgba(255,255,255,0.7)',
                    maxWidth: '560px',
                    lineHeight: 1.7,
                }}
            >
                We saw too many students stuck at Band 6.5, taking test after test without understanding
                why their scores weren't improving. IELTS BandBoost was created to fix that.
            </Typography>
        </Box>
    );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value }) {
    return (
        <Box
            sx={(theme) => ({
                backgroundColor: theme.palette.shades?.['clr-white-900'] || '#FFFFFF',
                border: `1px solid ${theme.palette.neutral?.['clr-200'] || '#D3D8DE'}`,
                borderRadius: '12px',
                padding: '20px 24px',
                width: '100%',
            })}
        >
            <Typography
                component="div"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.85rem',
                    color: '#6B7280',
                    mb: '6px',
                }}
            >
                {label}
            </Typography>
            <Typography
                component="div"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 800,
                    fontSize: '1.75rem',
                    color: '#111827',
                    lineHeight: 1.2,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

// ─── Mission Section ──────────────────────────────────────────────────────────

function MissionSection() {
    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                backgroundColor: theme.palette.shades?.['clr-white-900'] || '#FFFFFF',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: '64px',
                padding: '80px 64px',
                [theme.breakpoints.down('md')]: {
                    flexDirection: 'column',
                    padding: '48px 24px',
                    gap: '40px',
                },
            })}
        >
            {/* Left — text */}
            <Box sx={{ flex: 1, maxWidth: '480px' }}>
                <Typography
                    component="p"
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: '#0284C5',
                        letterSpacing: '0.05em',
                        mb: '12px',
                    }}
                >
                    Our Mission
                </Typography>

                <Typography
                    component="h2"
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        fontWeight: 800,
                        fontSize: '1.85rem',
                        color: '#111827',
                        lineHeight: 1.3,
                        mb: '24px',
                    }}
                >
                    Make IELTS Preparation Actually Work
                </Typography>

                <Typography
                    component="p"
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        fontWeight: 400,
                        fontSize: '0.95rem',
                        color: '#4B5563',
                        lineHeight: 1.75,
                        mb: '20px',
                    }}
                >
                    The traditional approach to IELTS preparation is broken. Students take dozens of
                    mock tests, watch countless YouTube videos, and still struggle to improve. Why?
                    Because they're practicing without understanding what's actually holding them back.
                </Typography>

                <Typography
                    component="p"
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        fontWeight: 400,
                        fontSize: '0.95rem',
                        color: '#4B5563',
                        lineHeight: 1.75,
                    }}
                >
                    IELTS BandBoost combines AI-powered analysis with a deep understanding of what
                    examiners actually look for. Every mock test becomes a learning opportunity, with
                    specific, actionable feedback that shows you exactly what to work on next.
                </Typography>
            </Box>

            {/* Right — stats */}
            <Box
                sx={(theme) => ({
                    flexShrink: 0,
                    width: '340px',
                    backgroundColor: '#EFF6FF',
                    borderRadius: '16px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    [theme.breakpoints.down('md')]: {
                        width: '100%',
                    },
                })}
            >
                <StatCard label="Students helped" value="50,000+" />
                <StatCard label="Average band improvement" value="+0.5 to +1.5" />
                <StatCard label="AI feedback accuracy" value="±0.5 bands" />
            </Box>
        </Box>
    );
}

// ─── Values Section ───────────────────────────────────────────────────────────

const VALUES = [
    {
        Icon: TrackChangesOutlinedIcon,
        title: 'Outcome-Focused',
        body: 'Every feature we build is designed around one goal: helping you reach your target IELTS band. No fluff, no distractions.',
    },
    {
        Icon: GroupOutlinedIcon,
        title: 'Built for Advanced Learners',
        body: 'We understand that you already know English. Our platform focuses on test-specific skills that advanced learners need to break through plateaus.',
    },
    {
        Icon: EmojiObjectsOutlinedIcon,
        title: 'Evidence-Based Improvement',
        body: 'Our AI feedback is based on official IELTS band descriptors and trained on thousands of scored responses. You get insights that matter.',
    },
    {
        Icon: VerifiedOutlinedIcon,
        title: 'Honest & Transparent',
        body: "We don't promise overnight miracles. IELTS improvement takes practice, and we provide the tools and feedback to make that practice effective.",
    },
];

function ValueCard({ Icon, title, body }) {
    return (
        <Box
            sx={(theme) => ({
                backgroundColor: theme.palette.shades?.['clr-white-900'] || '#FFFFFF',
                border: `1px solid ${theme.palette.neutral?.['clr-200'] || '#E5E7EB'}`,
                borderRadius: '12px',
                padding: '28px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
            })}
        >
            <Box
                sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor: '#DBEAFE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ color: '#0284C5', fontSize: '1.2rem' }} />
            </Box>
            <Typography
                component="h3"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#111827',
                }}
            >
                {title}
            </Typography>
            <Typography
                component="p"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.9rem',
                    color: '#6B7280',
                    lineHeight: 1.65,
                    mt: '-8px',
                }}
            >
                {body}
            </Typography>
        </Box>
    );
}

function ValuesSection() {
    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                backgroundColor: '#F8FAFC',
                padding: '80px 64px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                [theme.breakpoints.down('md')]: {
                    padding: '48px 24px',
                },
            })}
        >
            <Typography
                component="h2"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 800,
                    fontSize: '2rem',
                    color: '#111827',
                    mb: '12px',
                    textAlign: 'center',
                }}
            >
                What We Believe In
            </Typography>
            <Typography
                component="p"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.95rem',
                    color: '#6B7280',
                    mb: '48px',
                    textAlign: 'center',
                }}
            >
                Our approach to IELTS preparation is different because our values are different.
            </Typography>

            <Box
                sx={(theme) => ({
                    width: '100%',
                    maxWidth: '900px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '20px',
                    [theme.breakpoints.down('md')]: {
                        gridTemplateColumns: '1fr',
                    },
                })}
            >
                {VALUES.map((v) => (
                    <ValueCard key={v.title} {...v} />
                ))}
            </Box>
        </Box>
    );
}

// ─── Ready CTA Section ────────────────────────────────────────────────────────

function ReadyCTASection() {
    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#2C3E50',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 32px',
                textAlign: 'center',
            }}
        >
            <Typography
                component="h2"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 800,
                    fontSize: '1.75rem',
                    color: '#FFFFFF',
                    mb: '12px',
                }}
            >
                Ready to Experience the Difference?
            </Typography>
            <Typography
                component="p"
                sx={{
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.95rem',
                    color: 'rgba(255,255,255,0.65)',
                    mb: '32px',
                }}
            >
                Take a free mock test and see how AI-powered feedback can accelerate your IELTS journey.
            </Typography>
            <Box
                component="a"
                href="/practice#practice-by-tests"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#0284C5',
                    color: '#FFFFFF',
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    padding: '12px 28px',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        backgroundColor: '#026BA1',
                    },
                }}
            >
                Start Your Free Mock Test &nbsp;→
            </Box>
        </Box>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function About() {
    return (
        <AppShell hideAnnouncement placeAtTop>
            <AboutHero />
            <MissionSection />
            <ValuesSection />
            <ReadyCTASection />
        </AppShell>
    );
}

export default About;
